import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Sparkles, LayoutGrid, Calendar, User, Coins, X, Check, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent, trackPageView } from '../services/analytics';
import HexStory from './HexStory';
import DomainInsights from './DomainInsights';
import EventTimeline from './EventTimeline';
import ProfileTab from './ProfileTab';
import CreditsRefillModal from '../components/CreditsRefillModal';
import { useSession } from '../context/SessionContext';
import { purchaseProduct, PRODUCT_IDS, PRODUCT_DETAILS_BY_TYPE } from '../services/iapService';
import useDynamicPrices from '../hooks/useDynamicPrices';
import { rlog } from '../utils/railwayLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const BeautifulDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('dashboard_active_tab');
    if (saved) {
      localStorage.removeItem('dashboard_active_tab');
      return saved;
    }
    return 'mystory';
  });
  const { isPremium, isLoading, refreshSubscription, user } = useSession();
  const { getDisplayPrice } = useDynamicPrices();
  const [sageCredits, setSageCredits] = useState(0);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showTimelineUpgrade, setShowTimelineUpgrade] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);
  
  // DEVELOPMENT: Set to true to unlock all features for testing
  // WARNING: Keep this false in production!
  const DEV_UNLOCK_ALL = false;  // DISABLED FOR PRODUCTION
  
  // Use SessionContext (MongoDB) as source of truth for unlock status
  const isUnlocked = DEV_UNLOCK_ALL || isPremium;
  
  // Feature-specific unlock checks — re-reads localStorage on every render
  // so it picks up changes made by payment flow
  const [planUnlocks, setPlanUnlocks] = useState(() => {
    // Read synchronously on first render to avoid 1-frame "locked" flicker
    try { return JSON.parse(localStorage.getItem('plan_unlocks') || '[]'); } catch { return []; }
  });
  
  useEffect(() => {
    const readUnlocks = () => {
      try { setPlanUnlocks(JSON.parse(localStorage.getItem('plan_unlocks') || '[]')); } catch { setPlanUnlocks([]); }
    };
    readUnlocks();
    // Listen for storage changes (from payment flow)
    window.addEventListener('storage', readUnlocks);
    window.addEventListener('credits-updated', readUnlocks);
    return () => {
      window.removeEventListener('storage', readUnlocks);
      window.removeEventListener('credits-updated', readUnlocks);
    };
  }, [isPremium]);
  
  const isInsightsUnlocked = DEV_UNLOCK_ALL || (isPremium && planUnlocks.includes('life_insights'));
  const isTimelineUnlocked = DEV_UNLOCK_ALL || (isPremium && planUnlocks.includes('timeline'));
  
  const userId = user?.user_id || localStorage.getItem('user_id');

  // Refresh subscription status on mount to ensure we have latest from MongoDB
  useEffect(() => {
    rlog('dashboard_mount', {
      user_id: userId,
      is_premium: isPremium,
      is_unlocked: isUnlocked,
      plan_id: localStorage.getItem('plan_id'),
      plan_unlocks: localStorage.getItem('plan_unlocks'),
      credits_ls: localStorage.getItem('sage_credits'),
      insights_unlocked: isInsightsUnlocked,
      timeline_unlocked: isTimelineUnlocked,
    });
    (async () => {
      // Self-heal: if backend has plan_id but unlocks/credits missing, fix it.
      // CRITICAL: Skip ghost user_ids (device_id / session_id prefixes) — calling
      // self-heal on device_id is meaningless and wastes a round-trip.
      if (userId && !String(userId).startsWith('native-') && !String(userId).startsWith('web-') && !String(userId).startsWith('session_')) {
        try {
          const shStart = Date.now();
          await fetch(`${BACKEND_URL}/api/iap/self-heal/${userId}`, { method: 'POST' })
            .then(r => r.json())
            .then(d => {
              rlog('dashboard_selfheal_response', {
                user_id: userId,
                elapsed_ms: Date.now() - shStart,
                healed: !!d?.healed,
                healed_fields: d?.healed_fields,
                credits: d?.credits,
                plan: d?.plan,
              });
              if (d?.healed) {
                console.log('[Dashboard] Self-healed entitlements:', d.healed_fields);
              }
            })
            .catch((e) => rlog('dashboard_selfheal_error', { error: e?.message }));
        } catch {}
      }
      await refreshSubscription();
      rlog('dashboard_post_refresh', {
        user_id: userId,
        is_premium_after: localStorage.getItem('is_premium'),
        plan_id_after: localStorage.getItem('plan_id'),
        credits_after: localStorage.getItem('sage_credits'),
        plan_unlocks_after: localStorage.getItem('plan_unlocks'),
      });
    })();
    trackPageView('dashboard');
    trackEvent('dashboard_view', { user_id: userId, is_premium: isPremium });
  }, []);
  
  // Fetch Sage credits — single source of truth via localStorage + backend sync.
  // Reacts to 'credits-updated' (same-tab) and 'storage' (cross-tab) events so
  // the floating badge stays in sync with HexStory / Future Reels / purchases.
  useEffect(() => {
    let cancelled = false;
    
    const readAndSet = () => {
      const n = parseInt(localStorage.getItem('sage_credits') || '0', 10);
      if (!cancelled) setSageCredits(Number.isFinite(n) ? n : 0);
    };
    
    const fetchCredits = async () => {
      if (!userId || !isUnlocked) { readAndSet(); return; }
      // Skip device_id / session_id ghost IDs — they return 0 credits from
      // backend and would flicker the UI balance.
      if (String(userId).startsWith('native-') || String(userId).startsWith('web-') || String(userId).startsWith('session_')) {
        readAndSet();
        return;
      }
      
      // Always show localStorage first for instant feedback
      readAndSet();
      
      try {
        const response = await fetch(`${BACKEND_URL}/api/razorpay/subscription/${userId}`);
        const data = await response.json();
        if (cancelled) return;
        if (data.success && data.subscription) {
          const c = data.subscription.credits || 0;
          // Only trust backend if it has a non-zero value OR localStorage is also 0
          const localN = parseInt(localStorage.getItem('sage_credits') || '0', 10);
          if (c > 0 || localN === 0) {
            if (c !== localN) {
              rlog('credits_sync_diff', {
                user_id: userId,
                backend_credits: c,
                local_credits: localN,
                action: 'overwrite_local',
                source: 'dashboard_poll',
              });
            }
            setSageCredits(c);
            localStorage.setItem('sage_credits', String(c));
          } else if (c === 0 && localN > 0) {
            rlog('credits_backend_zero', {
              user_id: userId,
              backend_credits: c,
              local_credits: localN,
              action: 'keep_local',
            });
          }
        }
      } catch (error) {
        readAndSet();
      }
    };
    
    fetchCredits();
    // React immediately to credit changes from other screens (HexStory, PaymentSuccess, etc.)
    window.addEventListener('credits-updated', readAndSet);
    window.addEventListener('storage', readAndSet);
    // Poll every 30 seconds as a safety net (was 10s — reduced to cut backend load)
    const interval = setInterval(fetchCredits, 30000);
    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('credits-updated', readAndSet);
      window.removeEventListener('storage', readAndSet);
    };
  }, [userId, isUnlocked]);

  useEffect(() => {
    console.log('🔒 Dashboard unlock status:', isUnlocked ? 'UNLOCKED (premium)' : 'LOCKED', 'isPremium:', isPremium);
  }, [isUnlocked, isPremium]);

  const handleTabClick = (tabId) => {
    if (tabId === 'dashboard' && !isInsightsUnlocked) {
      navigate('/payment-plans');
      return;
    }
    if (tabId === 'timeline' && !isTimelineUnlocked) {
      // Timeline is only in Ultimate plan, but show all plans so user sees the upgrade path
      navigate('/payment-plans?source=timeline');
      return;
    }
    setActiveTab(tabId);
  };

  // Current plan detection
  const currentPlanName = (() => {
    if (!isPremium) return 'Free';
    if (planUnlocks.includes('timeline')) return 'Ultimate';
    if (planUnlocks.includes('future_reels')) return 'Premium';
    return 'Starter';
  })();

  const handleUpgradeToUltimate = async () => {
    setIsUpgrading(true);
    try {
      const result = await purchaseProduct(PRODUCT_IDS.ULTIMATE_999, userId);
      if (result.success) {
        toast.success('Ultimate Plan activated! Timeline unlocked');
        setShowTimelineUpgrade(false);
        await refreshSubscription();
        // Small delay for state to propagate
        setTimeout(() => setActiveTab('timeline'), 300);
      }
    } catch (err) {
      toast.error('Upgrade failed. Please try again.');
    } finally {
      setIsUpgrading(false);
    }
  };

  const tabs = [
    { id: 'mystory', name: 'Story', icon: Sparkles, locked: false },
    { id: 'dashboard', name: 'Insights', icon: LayoutGrid, locked: !isInsightsUnlocked },
    { id: 'timeline', name: 'Timeline', icon: Calendar, locked: !isTimelineUnlocked },
    { id: 'profile', name: 'Profile', icon: User, locked: false },
  ];

  const isEmergentDemo = localStorage.getItem('demo_source') === 'emergent';

  return (
    <div className={isEmergentDemo ? "min-h-screen" : "fixed inset-0 overflow-hidden"} style={{ background: 'radial-gradient(ellipse at center, #1a2332 0%, #0a0e17 100%)' }}>
      {/* Floating Credits Badge - visible on Insights, Timeline, Profile tabs */}
      {isUnlocked && activeTab !== 'mystory' && (
        <button
          onClick={() => setShowCreditsModal(true)}
          className="fixed top-3 right-3 z-50 flex items-center gap-1 bg-amber-500/20 border border-amber-500/40 rounded-full hover:bg-amber-500/30 transition-all backdrop-blur-sm"
          style={{ padding: 'clamp(4px, 1vh, 6px) clamp(8px, 2vw, 12px)' }}
          data-testid="floating-credits-badge"
        >
          <Coins className="w-3.5 h-3.5 text-amber-400" />
          <span style={{ fontSize: 'clamp(10px, 2.8vw, 13px)', fontWeight: '600', color: '#fbbf24' }}>{sageCredits}</span>
        </button>
      )}
      
      {/* Use display:none instead of conditional rendering to preserve component state */}
      <div style={{ display: activeTab === 'mystory' ? 'block' : 'none' }}><HexStory /></div>
      <div style={{ display: activeTab === 'dashboard' ? 'block' : 'none' }}><DomainInsights /></div>
      <div style={{ display: activeTab === 'timeline' ? 'block' : 'none' }}><EventTimeline /></div>
      <div style={{ display: activeTab === 'profile' ? 'block' : 'none' }}><ProfileTab /></div>
      
      {/* Modern Bottom Navigation */}
      <div data-kb-hide className="fixed bottom-0 left-0 right-0 z-40" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div style={{ margin: '0 clamp(8px, 2vw, 16px) clamp(8px, 1.5vh, 16px)' }}>
          <div style={{
            background: 'rgba(26, 35, 50, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 'clamp(10px, 3vw, 16px)',
            border: '1px solid rgba(116, 179, 255, 0.1)',
            boxShadow: '0 -4px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '600px',
            margin: '0 auto',
          }}>
            <div className="flex justify-around items-center" style={{ padding: 'clamp(4px, 0.8vh, 8px) clamp(4px, 1vw, 8px)' }}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    data-testid={`nav-tab-${tab.id}`}
                    style={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '2px',
                      padding: 'clamp(6px, 1.2vh, 10px) clamp(10px, 3vw, 16px)',
                      borderRadius: 'clamp(8px, 2vw, 12px)',
                      background: isActive ? 'rgba(116, 179, 255, 0.15)' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      opacity: tab.locked ? 0.4 : 1,
                    }}
                  >
                    <Icon style={{
                      width: 'clamp(16px, 4.5vw, 20px)',
                      height: 'clamp(16px, 4.5vw, 20px)',
                      color: isActive ? '#74B3FF' : 'rgba(226, 232, 240, 0.4)',
                      strokeWidth: isActive ? 2 : 1.5,
                    }} />
                    <span style={{
                      fontSize: 'clamp(8px, 2.2vw, 10px)',
                      letterSpacing: '0.5px',
                      color: isActive ? '#E2E8F0' : 'rgba(226, 232, 240, 0.4)',
                      fontWeight: isActive ? 500 : 400,
                    }}>{tab.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      
      {/* Credits Refill Modal */}
      <CreditsRefillModal
        isOpen={showCreditsModal}
        onClose={() => setShowCreditsModal(false)}
        userId={userId}
        currentCredits={sageCredits}
        onCreditsUpdated={(newBalance) => setSageCredits(newBalance)}
      />

      {/* Timeline Upgrade Modal */}
      <AnimatePresence>
        {showTimelineUpgrade && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}
            onClick={() => !isUpgrading && setShowTimelineUpgrade(false)}
            data-testid="timeline-upgrade-overlay"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              onClick={(e) => e.stopPropagation()}
              data-testid="timeline-upgrade-modal"
              style={{
                width: 'min(92vw, 380px)',
                background: 'linear-gradient(165deg, #141c2b 0%, #0d1420 60%, #0f1a2a 100%)',
                borderRadius: '20px',
                border: '1px solid rgba(116, 179, 255, 0.15)',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              {/* Top shimmer accent */}
              <div style={{
                position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
                background: 'linear-gradient(90deg, transparent, #74B3FF, #a78bfa, #74B3FF, transparent)',
              }} />

              {/* Close button */}
              <button
                onClick={() => setShowTimelineUpgrade(false)}
                data-testid="timeline-upgrade-close"
                style={{
                  position: 'absolute', top: '12px', right: '12px', zIndex: 10,
                  background: 'rgba(255,255,255,0.06)', border: 'none', borderRadius: '50%',
                  width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer',
                }}
              >
                <X style={{ width: '14px', height: '14px', color: 'rgba(226,232,240,0.5)' }} />
              </button>

              {/* Header section */}
              <div style={{ padding: '28px 24px 16px', textAlign: 'center' }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '14px', margin: '0 auto 14px',
                  background: 'linear-gradient(135deg, rgba(167,139,250,0.2), rgba(116,179,255,0.2))',
                  border: '1px solid rgba(167,139,250,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {/* clean - no icon */}
                </div>
                <h3 style={{
                  fontSize: 'clamp(17px, 4.5vw, 20px)', fontWeight: 700, color: '#E2E8F0',
                  margin: '0 0 4px', letterSpacing: '-0.3px',
                }}>Unlock Full Timeline</h3>
                <p style={{
                  fontSize: 'clamp(12px, 3vw, 13px)', color: 'rgba(148,163,184,0.8)',
                  margin: 0, lineHeight: 1.4,
                }}>
                  {currentPlanName !== 'Free' ? `You're on ${currentPlanName} plan. ` : ''}
                  Upgrade to Ultimate for complete life timeline access.
                </p>
              </div>

              {/* Benefits list */}
              <div style={{ padding: '0 24px 16px' }}>
                <div style={{
                  background: 'rgba(116,179,255,0.04)',
                  borderRadius: '12px',
                  border: '1px solid rgba(116,179,255,0.08)',
                  padding: '14px',
                }}>
                  {[
                    { text: 'Monthly & Yearly Predictions', highlight: true },
                    { text: 'Life Event Mapping', highlight: true },
                    { text: 'Mahadasha Transition Alerts', highlight: true },
                    { text: 'All Starter + Premium features', highlight: false },
                  ].map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '6px 0',
                      borderBottom: i < 3 ? '1px solid rgba(116,179,255,0.05)' : 'none',
                    }}>
                      <div style={{
                        width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                        background: item.highlight ? 'rgba(167,139,250,0.15)' : 'rgba(116,179,255,0.1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Check style={{
                          width: '11px', height: '11px',
                          color: item.highlight ? '#a78bfa' : '#74B3FF',
                        }} />
                      </div>
                      <span style={{
                        fontSize: 'clamp(12px, 3vw, 13px)', color: 'rgba(226,232,240,0.85)',
                        fontWeight: item.highlight ? 500 : 400,
                      }}>{item.text}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Price + CTA */}
              <div style={{ padding: '0 24px 24px' }}>
                <div style={{ textAlign: 'center', marginBottom: '14px' }}>
                  <span style={{ fontSize: 'clamp(26px, 7vw, 32px)', fontWeight: 800, color: '#E2E8F0' }}>
                    {getDisplayPrice('ultimate') || '—'}
                  </span>
                  <span style={{ fontSize: '13px', color: 'rgba(148,163,184,0.7)', marginLeft: '4px' }}>
                    /month
                  </span>
                </div>

                <button
                  onClick={handleUpgradeToUltimate}
                  disabled={isUpgrading}
                  data-testid="timeline-upgrade-buy-btn"
                  style={{
                    width: '100%', padding: 'clamp(12px, 2.5vh, 16px)',
                    borderRadius: '12px', border: 'none', cursor: isUpgrading ? 'wait' : 'pointer',
                    background: isUpgrading
                      ? 'rgba(167,139,250,0.3)'
                      : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                    color: '#fff', fontWeight: 700, fontSize: 'clamp(14px, 3.5vw, 15px)',
                    letterSpacing: '0.3px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                    transition: 'all 0.2s',
                    boxShadow: isUpgrading ? 'none' : '0 4px 20px rgba(124,58,237,0.3)',
                  }}
                >
                  {isUpgrading ? (
                    <>Processing...</>
                  ) : (
                    <>
                      <Zap style={{ width: '16px', height: '16px' }} />
                      Upgrade to Ultimate
                    </>
                  )}
                </button>

                <p style={{
                  textAlign: 'center', fontSize: '11px', color: 'rgba(148,163,184,0.5)',
                  marginTop: '10px', lineHeight: 1.3,
                }}>
                  Instant activation. Cancel anytime.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BeautifulDashboard;
